import { useMemo, useState } from "react";
import { DataAttributionNote } from "@/components/DataAttributionNote";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { BackButton } from "@/components/ui/BackButton";
import { addDays, format, parseISO, startOfWeek } from "date-fns";
import { listWorkouts } from "@/api/workouts";
import { Card, SectionLabel } from "@/components/ui/Card";
import { WorkoutRow } from "@/components/dashboard/WorkoutRow";
import {
  CalendarView,
  type CalendarWeekMeta,
  type CalendarWorkout,
} from "@/components/plan/CalendarView";
import { cn } from "@/lib/utils";
import { LoadError } from "@/components/ui/LoadError";

const FILTERS = [
  { id: null as string | null, labelKey: "history.filter_all" as const },
  { id: "swim", labelKey: "history.filter_swim" as const },
  { id: "bike", labelKey: "history.filter_bike" as const },
  { id: "run", labelKey: "history.filter_run" as const },
  { id: "strength", labelKey: "history.filter_strength" as const },
] as const;

export default function History() {
  const { t } = useTranslation();
  const [sport, setSport] = useState<string | null>(null);
  const [mode, setMode] = useState<"list" | "calendar">("list");

  const q = useQuery({
    queryKey: ["history", sport],
    queryFn: ({ signal }) => listWorkouts({ days: 90, limit: 100, sport }, signal),
  });
  const { data, isLoading } = q;

  const calendarWorkouts = useMemo<CalendarWorkout[]>(() => {
    if (!data) return [];
    return data.map((w) => ({
      id: w.id,
      date: w.date,
      sport: w.sport,
      title: w.title,
      duration_min: w.duration_min,
      tss: w.tss,
      source_unavailable: w.source_unavailable,
      unavailable_reason: w.unavailable_reason,
    }));
  }, [data]);

  /** Build 13 ISO-week rows for the last 90 days, newest week first. */
  const calendarWeeks = useMemo<CalendarWeekMeta[]>(() => {
    const todayMonday = startOfWeek(new Date(), { weekStartsOn: 1 });
    const weeks: CalendarWeekMeta[] = [];
    for (let i = 0; i < 13; i++) {
      const monday = addDays(todayMonday, -i * 7);
      weeks.push({
        weekNumber: i === 0 ? 0 : -i, // label 0 = current, -1 = last week, etc.
        startDate: format(monday, "yyyy-MM-dd"),
      });
    }
    // Cosmetic: number them human-friendly — "сегодня" week is W0, older W-1…
    return weeks;
  }, []);

  return (
    <div className="space-y-5">
      <BackButton to="/" label={t("history.back_dashboard")} withHome={false} />

      <div>
        <SectionLabel>{t("history.section_title")}</SectionLabel>
        <h1 className="mt-1 font-serif text-[26px] font-medium tracking-tight">
          {t("history.last_90d")}
        </h1>
      </div>

      {/* View toggle */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex rounded-full border border-cream-border bg-cream-light p-0.5">
          {(["list", "calendar"] as const).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={cn(
                "rounded-full px-3.5 py-1.5 text-[13px] transition",
                mode === m
                  ? "bg-navy text-cream-light"
                  : "text-taupe hover:text-navy",
              )}
            >
              {m === "list" ? t("history.view_list") : t("history.view_calendar")}
            </button>
          ))}
        </div>

        {mode === "list" ? (
          <div className="flex flex-wrap gap-1.5">
            {FILTERS.map((f) => (
              <button
                key={f.id ?? "all"}
                onClick={() => setSport(f.id)}
                className={cn(
                  "whitespace-nowrap rounded-full border px-3 py-1 text-[12px] transition",
                  sport === f.id
                    ? "border-amber bg-amber/15 text-amber-dark"
                    : "border-cream-border bg-cream-light text-taupe hover:text-navy",
                )}
              >
                {t(f.labelKey)}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {isLoading ? (
        <div className="py-10 text-center text-taupe">{t("history.loading")}</div>
      ) : q.isError ? (
        <LoadError onRetry={() => void q.refetch()} retrying={q.isFetching} />
      ) : !data || data.length === 0 ? (
        <Card>
          <div className="py-4 text-center text-[13px] text-taupe">
            {t("history.no_workouts")}
            <br />
            {t("history.connect_intervals")}
          </div>
        </Card>
      ) : mode === "list" ? (
        <>
          <div className="space-y-2">
            {data.map((w) => (
              <WorkoutRow key={w.id} workout={w} />
            ))}
          </div>
          <div className="pt-2 text-center text-[12px] text-taupe">
            {t("history.showing_n", { n: data.length })}
          </div>
        </>
      ) : (
        <CalendarView weeks={calendarWeeks} workouts={calendarWorkouts} />
      )}
      <DataAttributionNote />
    </div>
  );
}
